import { z } from 'zod';

// Numbers come back as numeric strings sometimes (Sequelize DECIMAL), so we
// coerce. All non-essential fields are nullish to degrade gracefully.
const numberish = z
    .union([z.number(), z.string()])
    .transform((v) => (typeof v === 'string' ? Number(v) : v))
    .pipe(z.number())
    .nullish();

// Publish lifecycle. `published` is the only customer-visible state;
// `draft`/`archived` are hard-hidden everywhere on the API side (storefront,
// search, feeds, cart) and non-grantable by any pricelist/audience rule.
export const PRODUCT_STATUSES = ['draft', 'published', 'archived'] as const;
export const ProductStatusSchema = z.enum(PRODUCT_STATUSES);
export type ProductStatus = z.infer<typeof ProductStatusSchema>;

export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
    draft: 'Draft',
    published: 'Publicat',
    archived: 'Arhivat',
};

export const ProductImageSchema = z.object({
    id: z.number().optional(),
    image: z.string(),
    position: z.number().optional().nullable(),
});

export type ProductImage = z.infer<typeof ProductImageSchema>;

// Per-warehouse stock row coming back from /products under `quantities`.
// stockAccountId 4 → Brasov (BV); everything else is Ploiesti (PL) per the
// legacy mapping in src/structures/Product.js. If a third warehouse ever
// shows up, expand this — the legacy code lumps "non-BV" into PL, so the v2
// list will mirror that for now.
export const ProductQuantitySchema = z.object({
    productId: z.number().optional(),
    stockAccountId: z.number(),
    quantity: z.number().int(),
    type: z.string().optional(),
});

export type ProductQuantity = z.infer<typeof ProductQuantitySchema>;

export const STOCK_ACCOUNT_BV = 4;
export const STOCK_ACCOUNT_PL = 1;

export function summarizeQuantities(quantities: ProductQuantity[] | null | undefined) {
    let bv = 0;
    let pl = 0;
    for (const q of quantities ?? []) {
        if (q.stockAccountId === STOCK_ACCOUNT_BV) bv += q.quantity;
        else pl += q.quantity;
    }
    return { bv, pl, total: bv + pl };
}

// A product counts as "newly stocked" when its last inventory entry landed
// within the window (default 7 days) — drives the "NOU" card badge.
export function isNewlyStocked(
    lastEntryDate: string | null | undefined,
    withinDays = 7,
): boolean {
    if (!lastEntryDate) return false;
    const entered = new Date(lastEntryDate).getTime();
    if (Number.isNaN(entered)) return false;
    return Date.now() - entered <= withinDays * 24 * 60 * 60 * 1000;
}

// Image entries used inside the form — either an existing `{id, image,
// position}` row, or a newly added one with `data` (base64 / dataURL) +
// position. `id` MUST round-trip for existing rows: the API's saveImages keys
// its keep-existing branch on it, and an id-less entry without `data` crashes
// the whole save transaction.
export const ProductFormImageSchema = z.object({
    id: z.number().optional(),
    image: z.string().optional(),
    data: z.string().optional(),
    position: z.number().nonnegative(),
    // Fabric design JSON carried out of the DesignEditor round-trip. Must be in
    // the schema or zodResolver strips it before submit; on save the API's
    // Product.saveImages upserts an ImageDesign row from it under the (new)
    // filename. Form-only — deliberately absent from the DB ProductImageSchema.
    _pendingDesignJson: z.string().optional(),
});

export type ProductFormImage = z.infer<typeof ProductFormImageSchema>;

// Slim tag shape as it comes back under a product's `tags` include. The join
// row Sequelize attaches (`ProductTag`) is ignored — we only read identity.
export const ProductTagSchema = z.object({
    id: z.number(),
    name: z.string(),
    slug: z.string().nullish(),
    color: z.string().nullish(),
});

export type ProductTag = z.infer<typeof ProductTagSchema>;

// Wire shape of a product's `attributes` include: Attribute rows with the
// ProductAttribute through row carrying the actual `value`. `type` is declared
// INTEGER on the API model but holds strings in practice ('text', 'text-area',
// 'enum', 'multiple-select-enum', 'rich-text-area') — tolerate both.
export const ProductAttributeValueSchema = z
    .object({
        id: z.number(),
        textId: z.string().nullish(),
        type: z.union([z.string(), z.number()]).nullish(),
        name: z.string().nullish(),
        description: z.string().nullish(),
        value: z.string().nullish(),
        ProductAttribute: z
            .object({
                value: z.string().nullish(),
                shortValue: z.string().nullish(),
            })
            .passthrough()
            .nullish(),
    })
    .passthrough();

export type ProductAttributeValue = z.infer<typeof ProductAttributeValueSchema>;

// v1 read `attribute.value || attribute.ProductAttribute.value` — keep parity.
export function productAttributeValue(attr: ProductAttributeValue): string {
    return attr.value || attr.ProductAttribute?.value || '';
}

// Wire shape of a product's `recipe` include: source Product rows with the
// ProductRecipe through row carrying sourceProductId/sourceQuantity.
export const ProductRecipeRowSchema = z
    .object({
        id: z.number().optional(),
        sku: z.string().nullish(),
        title: z.string().nullish(),
        sourceProductId: z.number().nullish(),
        sourceQuantity: numberish,
        ProductRecipe: z
            .object({
                sourceProductId: z.number().nullish(),
                sourceQuantity: numberish,
                weight: numberish,
            })
            .passthrough()
            .nullish(),
    })
    .passthrough();

export type ProductRecipeRow = z.infer<typeof ProductRecipeRowSchema>;

// Normalized recipe entry (mirrors v1 structures/Product.js:138-154, but with
// a numeric sourceProductId — 0 means an unlinked free-label row).
export type RecipeEntry = {
    sourceProductId: number;
    label: string;
    sourceQuantity: number;
};

export function normalizeRecipe(rows: ProductRecipeRow[] | null | undefined): RecipeEntry[] {
    return (rows ?? []).map((row) => ({
        sourceProductId: row.ProductRecipe?.sourceProductId ?? row.sourceProductId ?? 0,
        label: [row.sku, row.title].filter(Boolean).join(' - ') || 'Blank',
        sourceQuantity: Number(row.ProductRecipe?.sourceQuantity ?? row.sourceQuantity ?? 0),
    }));
}

export const ProductSchema = z.object({
    id: z.number(),
    title: z.string(),
    sku: z.string().nullish(),
    barcode: z.string().nullish(),
    slug: z.string().nullish(),
    description: z.string().nullish(),
    metaTitle: z.string().nullish(),
    metaDescription: z.string().nullish(),
    thumbnail: z.string().nullish(),

    categoryId: z.number().nullish(),
    unitId: z.number().nullish(),
    variantId: z.number().nullish(),
    availabilitySupplierId: z.number().nullish(),

    sourcePrice: numberish,
    totalPrice: numberish,
    totalOldPrice: numberish,
    vat: numberish,
    vatBracket: numberish,
    quantity: numberish,
    maxQuantity: numberish,
    preorderQuantity: numberish,
    views: numberish,

    active: z.union([z.boolean(), z.number()]).nullish(),
    activeOnline: z.union([z.boolean(), z.number()]).nullish(),
    status: ProductStatusSchema.nullish(),
    service: z.union([z.boolean(), z.number()]).nullish(),
    prepay: z.union([z.boolean(), z.number()]).nullish(),
    excludeFromFreeDelivery: z.union([z.boolean(), z.number()]).nullish(),
    preorderAvailable: z.union([z.boolean(), z.number()]).nullish(),

    availability: z.string().nullish(),
    availableFrom: z.string().nullish(),
    preorderStart: z.string().nullish(),
    preorderEnd: z.string().nullish(),

    images: z.array(ProductImageSchema).nullish(),
    quantities: z.array(ProductQuantitySchema).nullish(),
    tags: z.array(ProductTagSchema).nullish(),
    attributes: z.array(ProductAttributeValueSchema).nullish(),
    recipe: z.array(ProductRecipeRowSchema).nullish(),

    category: z
        .object({
            id: z.number(),
            title: z.string().nullish(),
            label: z.string().nullish(),
        })
        .nullish(),

    createdAt: z.string().nullish(),
    updatedAt: z.string().nullish(),
    // Denormalized MAX(Inventory.entryDate) — drives the "NOU" badge + the
    // "ultimele intrate in stoc" sort.
    lastEntryDate: z.string().nullish(),
});

export type Product = z.infer<typeof ProductSchema>;

export const ProductListSchema = z.object({
    data: z.array(ProductSchema),
    count: z.number().int().nonnegative(),
});

export type ProductList = z.infer<typeof ProductListSchema>;

// Args for GET /v2/products (the admin listing module). All filters optional;
// empty values are dropped before the request.
export type ListProductsArgs = {
    start?: number;
    limit?: number;
    q?: string;
    /** @deprecated alias of `q`, kept so pre-rebuild callers compile — prefer `q`. */
    search?: string;
    // Whitelisted server-side: id, title, price, sourcePrice, quantity, views,
    // score, lastEntryDate (alias 'lastStockEntry'), createdAt.
    sort?: string;
    direction?: 'asc' | 'desc';
    /** categoryHierarchyId LIKE-prefix (send the hierarchy string, not the numeric id). */
    categoryId?: string | number;
    status?: ProductStatus;
    availability?: string;
    /** Products having ANY of these tags. */
    tagIds?: number[];
    /** Explicit id set (deep-link chip hydration). */
    ids?: number[];
    supplierId?: number | string;
    priceMin?: number | string;
    priceMax?: number | string;
    /** '1' = with images, '0' = without, absent = all. */
    hasImage?: '0' | '1';
    /** 'in' = quantity > 0, 'out' = quantity <= 0 or null, absent = all. */
    stock?: 'in' | 'out';
    activeOnline?: '0' | '1';
    /** Only products that appear as items on this stock operation. */
    stockOperationId?: number | string;
};

// One attribute value inside the form — the API's saveAttributes accepts
// arrays of `{id, value}` (id = the Attribute id).
export const ProductFormAttributeSchema = z.object({
    id: z.number(),
    value: z.union([z.string(), z.number()]),
});

export type ProductFormAttribute = z.infer<typeof ProductFormAttributeSchema>;

// One recipe row inside the form. sourceProductId null = unlinked free-label
// row (serialized as 0, same as v1).
export const ProductFormRecipeRowSchema = z.object({
    sourceProductId: z.number().nullable(),
    label: z.string().default(''),
    sourceQuantity: z.coerce
        .number({ invalid_type_error: 'Cantitatea este obligatorie' })
        .int()
        .min(1, 'Cantitatea minima este 1'),
});

export type ProductFormRecipeRow = z.infer<typeof ProductFormRecipeRowSchema>;

// Form schema — what the create / edit form binds to and what gets shipped
// back to PUT /products[/:id]. Aligns with the legacy Structures.Product
// .export() shape.
export const ProductFormSchema = z.object({
    title: z.string().min(1, 'Numele este obligatoriu'),
    sku: z.string().min(1, 'SKU este obligatoriu'),
    barcode: z.string().min(1, 'Codul de bare este obligatoriu'),
    slug: z.string().min(1, 'SLUG este obligatoriu'),
    description: z.string().min(1, 'Descrierea este obligatorie'),
    metaTitle: z.string().min(1, 'Meta Titlul este obligatoriu'),
    metaDescription: z.string().min(1, 'Meta Descrierea este obligatorie'),
    categoryId: z.coerce.number({ invalid_type_error: 'Categoria este obligatorie' }).int().positive('Categoria este obligatorie'),
    unitId: z.coerce.number().int().positive().default(1),
    variantId: z.coerce.number().int().positive().nullable().optional(),
    availabilitySupplierId: z.coerce.number().int().positive().nullable().optional(),
    sourcePrice: z.coerce.number({ invalid_type_error: 'Pretul este obligatoriu' }).nonnegative('Pretul nu poate fi negativ'),
    vatBracket: z.coerce.number({ invalid_type_error: 'Cota TVA este obligatorie' }),
    // Tri-state, load-bearing for the storefront (client/utils/product.js):
    // null = nelimitat, 0 = doar grupuri, N = plafon. Empty input ('' or the
    // NaN that valueAsNumber produces for a cleared field) means null — never
    // default it to a number.
    maxQuantity: z.preprocess(
        (v) =>
            v === '' || v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v))
                ? null
                : v,
        z.coerce.number().int().nonnegative('Cantitatea nu poate fi negativa').nullable(),
    ),
    preorderQuantity: z.coerce.number().int().nonnegative().default(0),
    availability: z.enum(['unavailable', 'preorder', 'supplier']).default('unavailable'),
    status: ProductStatusSchema.default('draft'),
    active: z.boolean().default(true),
    activeOnline: z.boolean().default(false),
    service: z.boolean().default(false),
    prepay: z.boolean().default(false),
    // Product value doesn't count toward the free-delivery threshold.
    excludeFromFreeDelivery: z.boolean().default(false),
    preorderAvailable: z.boolean().default(false),
    availableFrom: z.string().nullable().optional(),
    preorderStart: z.string().nullable().optional(),
    preorderEnd: z.string().nullable().optional(),
    images: z.array(ProductFormImageSchema).default([]),
    tagIds: z.array(z.number()).default([]),
    // Both are OPTIONAL on purpose: `undefined` = key omitted from the payload
    // (the API keeps the existing rows — omit-means-keep), `[]` = clear all.
    // A form that never loaded them must leave them undefined.
    attributes: z.array(ProductFormAttributeSchema).optional(),
    recipe: z.array(ProductFormRecipeRowSchema).optional(),
});

export type ProductForm = z.infer<typeof ProductFormSchema>;

// --- Categories (slim, just for the dropdown) ---
export const CategorySchema = z.object({
    id: z.number(),
    title: z.string().nullish(),
    label: z.string().nullish(),
    parentId: z.number().nullish(),
    // Tree-encoded path used for subtree filtering (the API does
    // `categoryHierarchyId LIKE '<id>%'`). Filtering by `id` only matches the
    // exact node — useless for tree queries — so the products list filter must
    // send this field, not `id`.
    categoryHierarchyId: z.string().nullish(),
});

export type Category = z.infer<typeof CategorySchema>;

export const CategoryListSchema = z.object({
    data: z.array(CategorySchema),
    count: z.number().int().nonnegative().optional(),
});

// --- Suppliers (slim) ---
export const SupplierSchema = z.object({
    id: z.number(),
    name: z.string(),
});

export type Supplier = z.infer<typeof SupplierSchema>;

export const SupplierListSchema = z.object({
    data: z.array(SupplierSchema),
    count: z.number().int().nonnegative().optional(),
});

// --- Units (slim) ---
export const UnitSchema = z.object({
    id: z.number(),
    name: z.string(),
});

export type Unit = z.infer<typeof UnitSchema>;

export const UnitListSchema = z.object({
    data: z.array(UnitSchema),
    count: z.number().int().nonnegative().optional(),
});

// --- Stock accounts (GET /stock-accounts; 1 = Ploiesti, 4 = Brasov) ---
export const StockAccountSchema = z
    .object({
        id: z.number(),
        name: z.string(),
    })
    .passthrough();

export type StockAccount = z.infer<typeof StockAccountSchema>;

export const StockAccountListSchema = z.object({
    data: z.array(StockAccountSchema),
    count: z.number().int().nonnegative().optional(),
});

// --- Outstanding auto-transfer (GET /inventory/outstanding-transfer) ---
// NOTE: the endpoint answers `{ existing: StockOperation | null }`, NOT the
// usual `{ success, data }` envelope.
export const OutstandingTransferItemSchema = z
    .object({
        productId: z.number().nullish(),
        cardId: z.number().nullish(),
        stockAccountId: z.number().nullish(),
        quantity: numberish,
    })
    .passthrough();

export type OutstandingTransferItem = z.infer<typeof OutstandingTransferItemSchema>;

export const OutstandingTransferSchema = z
    .object({
        id: z.number(),
        items: z.array(OutstandingTransferItemSchema).nullish(),
    })
    .passthrough();

export type OutstandingTransfer = z.infer<typeof OutstandingTransferSchema>;

// --- Category detail (GET /categories/:id) — carries the attribute
// definitions (with enum values) the dynamic product-form section renders. ---
export const AttributeValueOptionSchema = z
    .object({
        id: z.number().optional(),
        attributeId: z.number().nullish(),
        value: z.string().nullish(),
    })
    .passthrough();

export type AttributeValueOption = z.infer<typeof AttributeValueOptionSchema>;

export const AttributeDefinitionSchema = z
    .object({
        id: z.number(),
        textId: z.string().nullish(),
        // 'text' | 'text-area' | 'rich-text-area' | 'enum' | 'multiple-select-enum'
        // (declared INTEGER on the model, string in the DB — tolerate both).
        type: z.union([z.string(), z.number()]).nullish(),
        name: z.string().nullish(),
        description: z.string().nullish(),
        values: z.array(AttributeValueOptionSchema).nullish(),
    })
    .passthrough();

export type AttributeDefinition = z.infer<typeof AttributeDefinitionSchema>;

export const CategoryDetailSchema = z
    .object({
        id: z.number(),
        title: z.string().nullish(),
        name: z.string().nullish(),
        label: z.string().nullish(),
        parentId: z.number().nullish(),
        categoryHierarchyId: z.string().nullish(),
        attributes: z.array(AttributeDefinitionSchema).nullish(),
    })
    .passthrough();

export type CategoryDetail = z.infer<typeof CategoryDetailSchema>;

// --- Bulk delete (POST /v2/products/bulk/delete) ---
export const BulkDeleteResultSchema = z.object({
    deleted: z.array(z.number()).default([]),
    failed: z
        .array(z.object({ id: z.number(), reason: z.string().nullish() }).passthrough())
        .default([]),
});

export type BulkDeleteResult = z.infer<typeof BulkDeleteResultSchema>;

// --- CSV import (POST /v2/products/import) ---
export type ImportProductsCsvArgs = {
    csv: string;
    dryRun?: boolean;
    /** Line numbers to CREATE on apply — server default is none, send explicitly. */
    createLines?: number[];
    /** Line numbers to UPDATE on apply — server default is all matched. */
    updateLines?: number[];
    options?: {
        updateExisting?: boolean;
        fetchImages?: boolean;
    };
};

export const CsvImportChangeSchema = z
    .object({
        field: z.string(),
        from: z.unknown().nullish(),
        to: z.unknown().nullish(),
    })
    .passthrough();

export type CsvImportChange = z.infer<typeof CsvImportChangeSchema>;

export const CsvImportUpdateSchema = z
    .object({
        line: z.number(),
        barcode: z.string(),
        id: z.number(),
        title: z.string().nullish(),
        changes: z.array(CsvImportChangeSchema).default([]),
        imageUrls: z.array(z.string()).default([]),
    })
    .passthrough();

export type CsvImportUpdate = z.infer<typeof CsvImportUpdateSchema>;

export const CsvImportCreateSchema = z
    .object({
        line: z.number(),
        barcode: z.string(),
        title: z.string().nullish(),
        fields: z.record(z.unknown()).default({}),
        imageUrls: z.array(z.string()).default([]),
    })
    .passthrough();

export type CsvImportCreate = z.infer<typeof CsvImportCreateSchema>;

export const CsvImportInvalidRowSchema = z
    .object({
        line: z.number(),
        reason: z.string(),
    })
    .passthrough();

export type CsvImportInvalidRow = z.infer<typeof CsvImportInvalidRowSchema>;

export const CsvImportPreviewSchema = z.object({
    updates: z.array(CsvImportUpdateSchema).default([]),
    creates: z.array(CsvImportCreateSchema).default([]),
    invalid: z.array(CsvImportInvalidRowSchema).default([]),
    summary: z
        .object({
            toUpdate: z.number(),
            toCreate: z.number(),
            images: z.number(),
        })
        .passthrough(),
});

export type CsvImportPreview = z.infer<typeof CsvImportPreviewSchema>;

export const CsvImportApplyResultSchema = z.object({
    updated: z.array(z.number()).default([]),
    created: z.array(z.number()).default([]),
    errors: z.array(CsvImportInvalidRowSchema).default([]),
});

export type CsvImportApplyResult = z.infer<typeof CsvImportApplyResultSchema>;

export type ImportProductsCsvResult = CsvImportPreview | CsvImportApplyResult;

// The endpoint answers with the preview shape on dryRun and the apply shape
// otherwise — discriminate on `summary`.
export function isCsvImportPreview(r: ImportProductsCsvResult): r is CsvImportPreview {
    return 'summary' in r;
}

